import { appConfig } from './../app.config';
import { PageNotFoundService } from './error-pages/page-not-found.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { BaseUtils } from './core/baseUtils.service';
import { AppService } from '../app.service';

@Injectable()
export class SharedService {
  authenticationToken: string;
  httpOptions: any = {};
  httpBlobOptions: any = {};
  constructor(
    public http: HttpClient,
    public router: Router,
    public messageService: MessageService,
    public appService: AppService,
    public pageNotFoundService: PageNotFoundService,
    public baseUtils: BaseUtils) {
  }

  private createAuthHeaders(options?: { blob?: boolean, analytic?: boolean, formData?: boolean }): HttpHeaders {
    const loginInUserDetail = JSON.parse(localStorage.getItem('loggedInUser'));
    const token = loginInUserDetail ? loginInUserDetail.Authorization : '';
    this.authenticationToken = token;
    let headersConfig: any = {};

    if (options?.analytic) {
      headersConfig = {
        'Content-Type': 'application/json',
        'auth': token,
        'Authorization': token,
        'Access-Control-Allow-Origin': '*'
      };
    } else if (options?.blob || options?.formData) {
      headersConfig = {
        'Authorization': token
      };
    } else {
      headersConfig = {
        'Content-Type': 'application/json',
        'Authorization': token,
        'Access-Control-Allow-Origin': '*'
      };
    }

    return new HttpHeaders(headersConfig);
  }

  /**
   * For getting logged in user authorization token from localstorage
   */
  getUserAuthentication(showLoadingSpinner: boolean = false) {
    if (showLoadingSpinner) {
      this.baseUtils.loading_spinner(true);
    }
    this.httpOptions = {
      headers: this.createAuthHeaders()
    };
    return this.httpOptions;
  }
  getUserAuthenticationForAnalytic() {
    this.httpOptions = {
      headers: this.createAuthHeaders({ analytic: true })
    };
    return this.httpOptions;
  }

  getUserAuthenticationWithoutSpinner() {
    this.baseUtils.loading_spinner(false);
    this.httpOptions = {
      headers: this.createAuthHeaders()
    }
    return this.httpOptions;
  }

  getBlobAuthentication() {
    this.baseUtils.loading_spinner(true);
    this.httpBlobOptions = {
      headers: this.createAuthHeaders({ blob: true })
    };
    return this.httpBlobOptions;
  }

  segregateSuccessData(data) {
    if (data && (data.isSuccess || data.success)) {
      return data;
    }
  }

  /**
   * Get data call
   * @param url API Url
   */
  getData(url, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    return this.http.get<any>(url, this.httpOptions).pipe(
      tap(
        data => {
          this.segregateSuccessData(data);
        },
        error => {
          this.error(error);
        }
      )
    );
  }

  /**
   * Get data call with custom headers
   * @param url API Url
   */
  getDataWithHeaders(url: any): Observable<any> {
    this.getUserAuthentication();
    const newHttpOptions = { ...this.httpOptions, observe: 'response' };
    return this.http.get<any>(url, newHttpOptions).pipe(
      tap(
        data => {
          this.segregateSuccessData(data);
        },
        error => this.error(error)
      )
    );
  }

  /**
   * Post data call
   * @param data Data to send
   * @param url API Url
   */
  postData(url, data?, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    return this.http.post<any>(url, data, this.httpOptions).pipe(
      tap( // Log the result or error
        result => {
          this.segregateSuccessData(result);
        },
        error => this.error(error)
      )
    );
  }

  postDataForForgotPassword(url, data?, showLoadingSpinner: boolean = false) {
    this.getUserAuthentication(showLoadingSpinner);
    return this.http.post<any>(url, data).pipe(
      tap(
        result => {
          if (!result) {
            this.baseUtils.toasterMessageAction('error', 'Account with entered email ID not found. Please try again');
          }
          this.segregateSuccessData(result);
        },
        error => {
          this.error(error);
        }
      )
    )
  }

  deleteDataWithObj(url, deleteObj?, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    return this.http.request("DELETE", url, { body: deleteObj, ...this.httpOptions })
      .pipe(
        tap( // Log the result or error
          data => {
            this.segregateSuccessData(data);
          },
          error => this.error(error)
        ));
  }

  getDataForAnalytics(url): Observable<any> {
    this.getUserAuthenticationForAnalytic();
    return this.http.get<any>(url, this.httpOptions).pipe(
      tap( // Log the result or error
        data => {
          this.segregateSuccessData(data);
        },
        error => this.error(error)
      )
    );
  }

  getDataForTransaction(url): Observable<any> {
    this.getUserAuthenticationWithoutSpinner();
    return this.http.get<any>(url, this.httpOptions).pipe(
      tap(
        data => {
          this.segregateSuccessData(data);
        },
        error => this.error(error)
      )
    );
  }

  /**
   * Post data call
   * @param data Data to send
   * @param url API Url
   */
  putData(url, data?, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    return this.http.put<any>(url, data, this.httpOptions).pipe(
      tap( // Log the result or error
        result => {
          this.segregateSuccessData(result);
        },
        error => this.error(error)
      )
    );
  }

  putDataWithCustomHeaderToken(url, data, token, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    return this.http.put<any>(url, data, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': token
      })
    }).pipe(
      tap( // Log the result or error
        result => {
          this.segregateSuccessData(result);
        },
        error => this.error(error)
      )
    );
  }

  /**
   * Delete Data call
   * @param url API Url
   */
  deleteData(url, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    return this.http.delete<any>(url, this.httpOptions)
      .pipe(
        tap( // Log the result or error
          data => {
            this.segregateSuccessData(data);
          },
          error => this.error(error)
        )
      );
  }

  /**
   * Error handling method
   * @param error (error returned from backend)
   */
  error(error: Response | any) {
    this.baseUtils.loading_spinner(false);
    console.log('Global Network Call Handling - ', error);
    let responseHeaders = error?.headers;
    let errorMessage = responseHeaders?.get('message') ? responseHeaders.get('message') : '';
    switch (error.status) {
      case 0:
        this.baseUtils.toasterMessageAction("error", "Connection to the server unsuccessful, please log in again");
        this.appService.redirectToLogin();
        break;
      case 400:
        if (errorMessage && errorMessage !== '') {
          this.baseUtils.toasterMessageAction("error", errorMessage);
        } else {
          this.baseUtils.toasterMessageAction("error", "There is a problem processing your request. Please try again later.");
        }
        break;
      case 401:
        this.baseUtils.toasterMessageAction("error", "Your session time has expired, please log in again");
        this.appService.redirectToLogin();
        break;
      case 403:
        if (errorMessage && errorMessage !== '') {
          this.baseUtils.toasterMessageAction("error", errorMessage);
        } else if (error.error?.message) {
          this.baseUtils.toasterMessageAction("error", error.error.message);
        } else {
          this.baseUtils.toasterMessageAction("error", "Permission Denied");
        }
        break;
      case 404:
        if (error.error?.message) {
          this.baseUtils.toasterMessageAction("error", error.error.message);
        } else {
          this.baseUtils.toasterMessageAction("error", "Requested resource not found");
        }
        break;
      case 409:
        if (errorMessage && errorMessage !== '') {
          this.baseUtils.toasterMessageAction("error", errorMessage);
        } else {
          this.baseUtils.toasterMessageAction("error", "Request can not be completed due to conflict");
        }
        break;
      case 422:
        if (errorMessage && errorMessage !== '') {
          this.baseUtils.toasterMessageAction("error", errorMessage);
        } else {
          this.baseUtils.toasterMessageAction("error", "Request not complete");
        }
        break
      case 423:
        if (error?.error?.message) {

          // in case of preset locked error, slight modification is done in api message
          let msg: string = error?.error?.message
          const isPresetLockedErr: boolean = msg.startsWith('Preset is currently locked')
          if (isPresetLockedErr) {
            msg = 'Preset is currently locked'
          }
          this.baseUtils.toasterMessageAction("error", msg);
        } else {
          this.baseUtils.toasterMessageAction("error", "There is a problem processing your request. Please try again later.");
        }
        break
      default:
        this.baseUtils.toasterMessageAction("error", "There is a problem processing your request. Please try again later.");
    }
    throw error;
  }

  getCommonGraphqlData(data: any) {
    let getCommonDataUrl = appConfig.getCommonDataByGraphQl;
    return this.postData(getCommonDataUrl, data);
  }

  /**
   * serverErrorHandler will call the error handler method
   * @param error Server error
   */
  serverErrorHandler(error, type?) {
    this.pageNotFoundService.errorHandler(error, type);
  }

  /**
   * To convert image into blob for displaying in Company logo
   */
  loadImageFromBlob(companyId, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    let imageUrl = appConfig.updateCompanyLogo;
    imageUrl = imageUrl.replace(':companyId', companyId);
    return this.http.get<Blob>(imageUrl, { headers: new HttpHeaders({ 'Authorization': this.authenticationToken }), responseType: 'blob' as 'json' }).pipe(
      tap( // Log the result or error
        data => {
          return data;
        },
        error => this.error(error)
      )
    );
  }

  switchBack() {
    const getSwitchBackUrl = appConfig.switchBack;
    return this.postData(getSwitchBackUrl, {});
  }

  /**
   * To convert image into blob for displaying in User profile
   */
  getProfileImageFromBlob(): Observable<any> {
    this.getUserAuthentication(false);
    const imageUrl = appConfig.updateUserImage;
    return this.http.get<Blob>(imageUrl, { headers: new HttpHeaders({ 'Authorization': this.authenticationToken }), responseType: 'blob' as 'json' }).pipe(
      tap( // Log the result or error
        data => {
          return data;
        },
        error => this.error(error)
      )
    );
  }

  getLogFileFromBlob(data: Object): Observable<any> {
    let logFileUrl = appConfig.downloadConnectorLogs;
    logFileUrl = logFileUrl.replace(':regionLocationId', data['regionLocationId'])
      .replace(':fileName', data['fileName']).replace(':fileType', data['fileType']);
    return this.http.get<Blob>(logFileUrl,
      { headers: new HttpHeaders({ 'Authorization': this.authenticationToken }), responseType: 'blob' as 'json' }).pipe(
        tap( // Log the result or error
          result => {
            return result;
          },
          error => this.error(error)
        )
      );
  }

  getPdfFileFromBlob(reportUrl: any): Observable<any> {
    this.getUserAuthentication(false);
    return this.http.get<Blob>(reportUrl,
      { headers: new HttpHeaders({ 'Authorization': this.authenticationToken }), responseType: 'blob' as 'json' }).pipe(
        tap( // Log the result or error
          data => {
            return data;
          },
          error => this.error(error)
        )
      );
  }

  getSupportFileFromBlob(data: Object): Observable<any> {
    let supportFileUrl = appConfig.downloadConnectorSupportLogs;
    supportFileUrl = supportFileUrl.replace(':regionLocationId', data['regionLocationId'])
      .replace(':fileName', data['fileName']).replace(':fileType', data['fileType']);
    return this.http.get<Blob>(supportFileUrl,
      { headers: new HttpHeaders({ 'Authorization': this.authenticationToken }), responseType: 'blob' as 'json' }).pipe(
        tap( // Log the result or error
          result => {
            return result;
          },
          error => this.error(error)
        )
      );
  }

  postDataWithHeaders(url, data?, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    const newHttpOptions = { ...this.httpOptions, observe: 'response' };
    return this.http.post<any>(url, data, newHttpOptions).pipe(
      tap(
        data => {
          this.segregateSuccessData(data);
        },
        error => this.error(error)
      )
    );
  }

  postFormDataWithHeaders(url, data?: FormData, showLoadingSpinner: boolean = false, handleGlobalError: boolean = true): Observable<any> {
    if (showLoadingSpinner) {
      this.baseUtils.loading_spinner(true);
    }
    const formDataOptions: any = {
      headers: this.createAuthHeaders({ formData: true }),
      observe: 'response'
    };
    return this.http.post<any>(url, data, formDataOptions).pipe(
      tap(
        result => {
          this.segregateSuccessData(result);
        },
        error => {
          if (handleGlobalError) {
            this.error(error);
          } else {
            this.baseUtils.loading_spinner(false);
          }
        }
      )
    );
  }

  putDataWithHeaders(url, data?, showLoadingSpinner: boolean = false): Observable<any> {
    this.getUserAuthentication(showLoadingSpinner);
    const newHttpOptions = { ...this.httpOptions, observe: 'response' };
    return this.http.put<any>(url, data, newHttpOptions).pipe(
      tap(
        result => {
          this.segregateSuccessData(result);
        },
        error => this.error(error)
      )
    );
  }

}
